/* ============================================================
   Dathent — Library (new, pack-aligned)
   Replaces old Content.jsx layout. Card component is pack's LibraryCard.
   Data source: useContentFeedContext (existing filters/data hooks preserved).
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import LibraryCard from '../components/library/LibraryCard';
import TikTokStatisticsBanner from '../components/metrics/TikTokStatisticsBanner.jsx';
import { isHeldAwaitingImage } from '../utils/heldPost.js';
import { aggregateMetrics, engagementRate, formatMetric, normalizeMetrics, readMetric } from '../lib/socialMetrics.js';
import { targetStatusToUi } from '../lib/postStatus.js';
import { platformDisplayName } from '../lib/platforms.js';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function deriveDisplayDate(item, t) {
  const status = item?.status || item?.contentItem?.status;
  const ps = item?.publishSummary;
  const when = ps?.publishedAt || ps?.scheduledAt || item?.publishedAt || item?.scheduledAt || item?.contentItem?.updatedAt;
  if (!when) return t('library.draft');
  const d = new Date(when);
  if (Number.isNaN(d.getTime())) return String(when);
  const prefix = status === 'published' ? '' : status === 'draft' ? t('library.draftPrefix') : '';
  const date = `${d.toLocaleString('en', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${prefix}${date}`;
}

function derivePlatform(item) {
  const targets = item?.publishSummary?.targets;
  if (Array.isArray(targets) && targets.length > 0) {
    return PLATFORM_FROM_NAME(targets[0].platform);
  }
  return PLATFORM_FROM_NAME(item?.platform || item?.platforms?.[0] || item?.contentItem?.platform) || item?.platform || 'linkedin';
}

/**
 * Expand a content row into per-platform display cards.
 *
 * Source of truth is the union of two sets:
 *   1. The originating batch platforms (`row.platforms`) — i.e. what the user
 *      originally generated this content for.
 *   2. The actual publish_targets — the rows the user has scheduled / published.
 *
 * Earlier this function fell into two mutually-exclusive branches: if any
 * publish_target existed, *only* targets became cards. That meant scheduling
 * the Telegram variant of a multi-platform draft made the Instagram sibling
 * card vanish from Library, even though the content_item still has IG in its
 * batch.platforms list. Now we keep the IG card as a virtual draft so users
 * can still open it and schedule independently.
 *
 * Returns: [{ key, contentRow, targetId, platform, status, scheduledAt, publishedAt, metrics }]
 */
function expandContentToCards(row) {
  // `row` is the mapped row from ContentFeedContext.mapContentRow — it has
  // an already-resolved `platform` and `platforms[]` derived from publishSummary
  // → batchPlatforms → format fallback. Prefer it over the raw contentItem.
  const item = row?.contentItem || row;
  const targets = row?.publishSummary?.targets || [];
  const rowPlatform = row?.platform || row?.platforms?.[0];
  const rowPlatforms = row?.platforms?.length ? row.platforms : (rowPlatform ? [rowPlatform] : []);

  // Bucket publish_targets by normalized platform name. Two Telegram channels
  // produce one bucket with two entries; we want both to show as separate
  // cards so the user can see "TG · Channel A" and "TG · Channel B" rows.
  const targetsByPlatform = new Map();
  for (const t of targets) {
    const p = PLATFORM_FROM_NAME(t.platform) || t.platform;
    if (!p) continue;
    if (!targetsByPlatform.has(p)) targetsByPlatform.set(p, []);
    targetsByPlatform.get(p).push(t);
  }

  // Union: every platform the batch was generated for + every platform that
  // already has a publish_target (handles the case where a target exists for
  // a platform that wasn't in the original batch — rare but possible).
  const platformOrder = [];
  const seen = new Set();
  for (const p of rowPlatforms) {
    const norm = PLATFORM_FROM_NAME(p) || p;
    if (norm && !seen.has(norm)) { seen.add(norm); platformOrder.push(norm); }
  }
  for (const p of targetsByPlatform.keys()) {
    if (!seen.has(p)) { seen.add(p); platformOrder.push(p); }
  }

  // Empty fallback — no batch platforms, no targets. Render one card with
  // whatever platform we can guess from the content_item.
  if (platformOrder.length === 0) {
    return [{
      key: String(item.id),
      contentRow: row,
      targetId: null,
      platform: PLATFORM_FROM_NAME(rowPlatform || item?.platform) || rowPlatform || 'linkedin',
      status: isHeldAwaitingImage(row) ? 'held' : (item.status || 'draft'),
      scheduledAt: null,
      publishedAt: null,
      metrics: null,
    }];
  }

  // Per-platform status for a virtual card (no publish_target yet for this
  // platform). We never reuse item.status === 'scheduled' here — that flag
  // flips as soon as ANY platform is scheduled, but for the others the right
  // user-facing state is still "Draft, click to schedule". Only true
  // item-level lifecycle states (generating / failed / archived) override.
  const virtualStatus = (() => {
    const s = item?.status;
    if (s === 'generating' || s === 'failed' || s === 'archived') return s;
    // TASK-074: text ready but the visual failed with no media — the post is
    // HELD ("waiting for image") until the user adds one or opts into a
    // text-only publish. Only applies to cards without a publish_target.
    if (isHeldAwaitingImage(row)) return 'held';
    return 'draft';
  })();

  const cards = [];
  for (const p of platformOrder) {
    const bucket = targetsByPlatform.get(p) || [];
    if (bucket.length === 0) {
      // No publish_target yet for this platform → virtual draft card.
      cards.push({
        key: `${item.id}::${p}`,
        contentRow: row,
        targetId: null,
        platform: p,
        status: virtualStatus,
        scheduledAt: null,
        publishedAt: null,
        metrics: null,
      });
      continue;
    }
    // One card per target (two TG accounts → two cards).
    for (const t of bucket) {
      cards.push({
        key: `${item.id}::${t.recoveryRootTargetId || t.id || p}`,
        contentRow: row,
        targetId: t.id,
        routeTargetId: t.recoveryRootTargetId || t.id,
        platform: p,
        status: t.status === 'queued' || t.status === 'pending' || t.status === 'claimed' ? 'scheduled'
          : t.status === 'needs_reconnect' ? 'attention'
          // TASK-331 (M7 fix): a lapsed org's paused target — LibraryCard's
          // STATUS_META.blocked_billing renders it with neutral styling.
          // (round 4: the round-3 'missed' status is retired — a past-dated
          // target the renewal sweep skipped while lapsed now arrives as a
          // plain 'failed' target and is caught by the `t.status === 'failed'`
          // branch above like any other failure.)
          : t.status === 'blocked_billing' ? t.status
          : targetStatusToUi(t.status, { platform: p, errorCode: t.lastErrorCode }) || 'needs_check',
        scheduledAt: t.scheduledAt,
        publishedAt: t.publishedAt,
        metrics: normalizeMetrics(t.metrics, t.metricFetch),
      });
    }
  }
  return cards;
}

// Per-card date: prefer the target's published/scheduled time so each platform
// card reflects its own timing instead of the content_item's overall timestamps.
function deriveCardDate(card, t) {
  const when = card.publishedAt || card.scheduledAt;
  if (when) {
    const d = new Date(when);
    if (!Number.isNaN(d.getTime())) {
      const prefix = card.status === 'published' ? '' : card.status === 'draft' ? t('library.draftPrefix') : '';
      const date = `${d.toLocaleString('en', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      return `${prefix}${date}`;
    }
  }
  return deriveDisplayDate(card.contentRow, t);
}

// Library ordering: NEWEST FIRST by the card's effective date — published time,
// else scheduled time, else last-updated. Without this the list rendered in raw
// feed order (≈ creation order), so old posts (past Ready/Failed from earlier
// runs) surfaced above a freshly-generated autopilot week. Plain descending puts
// the latest + upcoming at the top and old content at the bottom.
function libraryOrderTs(card) {
  const pub = Date.parse(card.publishedAt || '');
  if (Number.isFinite(pub)) return pub;
  const sched = Date.parse(card.scheduledAt || '');
  if (Number.isFinite(sched)) return sched;
  const upd = Date.parse(card.contentRow?.contentItem?.updatedAt || card.contentRow?.updatedAt || '');
  return Number.isFinite(upd) ? upd : 0;
}

// Only show a media kind badge for meaningful types (carousel/video/thread).
// The default "Image" badge is suppressed — it's visual noise when most posts
// don't carry a custom image and the cover falls back to the platform gradient.
function deriveMedia(item) {
  const format = item?.contentItem?.format || item?.format;
  if (!format) return null;
  const f = String(format).toLowerCase();
  if (f.includes('carousel') || f.includes('slide')) return 'carousel';
  if (f.includes('video') || f.includes('reel') || f.includes('short')) return 'video';
  if (f.includes('thread')) return 'thread';
  return null;
}

// Strip the "Generating[ optional N/N]: " prefix that leaks through when the
// item's title/body is just the raw generation prompt (e.g. failed/in-flight items).
function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

// Truncate to N chars at a word boundary + ellipsis (keeps card titles tidy).
function truncate(s, max) {
  if (!s || s.length <= max) return s || '';
  const sliced = s.slice(0, max);
  const lastSpace = sliced.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? sliced.slice(0, lastSpace) : sliced).replace(/[.,;:!?\s]+$/, '') + '…';
}

// Resolve the platform-specific copy when activeVersion.platformVersions has
// it. Backend stores per-platform variants in content_versions.platform_versions_json,
// then compacts to 240-char snippets for the list endpoint. Falls back to the
// shared body/caption/hook when no per-platform variant exists.
function platformBody(version, platform) {
  if (!version || !platform) return null;
  const pv = version.platformVersions || {};
  const aliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const direct = pv[platform];
  if (typeof direct === 'string' && direct.trim()) return direct;
  if (direct && typeof direct === 'object' && typeof direct.body === 'string' && direct.body.trim()) return direct.body;
  if (aliasKey) {
    const aliased = pv[aliasKey];
    if (typeof aliased === 'string' && aliased.trim()) return aliased;
    if (aliased && typeof aliased === 'object' && typeof aliased.body === 'string' && aliased.body.trim()) return aliased.body;
  }
  return null;
}

// Prefer the AI-generated copy (activeVersion.hook|body|caption) over the raw
// generation prompt stored on contentItem (which is often the user's input).
// When the version has a per-platform variant (Threads/X/Telegram length caps,
// platform tone), use that one for THIS card so each platform card on a
// multi-target post shows its own copy.
function deriveTitle(item, platform = null, t) {
  const v = item?.activeVersion;
  const platformText = platformBody(v, platform);
  const hookOrBody = platformText || v?.hook || v?.body || v?.caption;
  const raw = hookOrBody
    || item?.contentItem?.title
    || item?.title
    || item?.contentItem?.body
    || item?.body
    || '';
  const cleaned = cleanPromptPrefix(raw).split('\n')[0];
  return truncate(cleaned, 60) || t('library.untitledDraft');
}

function deriveSnippet(item, platform = null) {
  const v = item?.activeVersion;
  const platformText = platformBody(v, platform);
  const body = platformText || v?.body || v?.caption || v?.hook;
  if (body) return cleanPromptPrefix(body);
  return cleanPromptPrefix(item?.contentItem?.body || item?.body || '');
}

function deriveCoverImage(item) {
  return item?.mediaPreview?.url
    || item?.activeVersion?.imageUrl
    || item?.activeVersion?.thumbnailUrl
    || item?.mediaUrls?.[0]
    || null;
}

export function deriveVideoSource(item) {
  const url = item?.mediaPreview?.url || item?.activeVersion?.mediaUrls?.[0] || item?.mediaUrls?.[0];
  const type = String(item?.raw?.mediaPreview?.type || item?.activeVersion?.mediaPreview?.type || item?.mediaPreview?.type || '').toLowerCase();
  if (!url || /\.(?:png|jpe?g|gif|webp|avif)(?:[?#]|$)/i.test(url)) return null;
  if (type.startsWith('image')) return null;
  if (!type.includes('video') && !/\.(?:mp4|mov|webm)(?:[?#]|$)/i.test(url) && deriveMedia(item) !== 'video') return null;
  return url;
}

export function deriveVideoPoster(item, videoUrl) {
  if (!videoUrl) return deriveCoverImage(item);
  const candidates = [item?.mediaPreview?.thumbnailUrl, item?.activeVersion?.thumbnailUrl, item?.activeVersion?.imageUrl];
  return candidates.find((url) => url && url !== videoUrl) || null;
}

const TABS = [
  { id: 'all', labelKey: 'library.tabAll', status: 'all' },
  { id: 'published', labelKey: 'library.tabPublished', status: 'published' },
  { id: 'scheduled', labelKey: 'library.tabScheduled', status: 'scheduled' },
  { id: 'draft', labelKey: 'library.tabDrafts', status: 'draft' },
  { id: 'attention', labelKey: 'library.tabNeedsAttention', status: 'failed' },
];

const PLATFORMS = ['all', 'instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'threads', 'telegram'];

export const Library = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { content, counts, loading, hasLoaded, error, hasMore, loadMore, refreshContent, metricRefreshFeedback } = useContentFeedContext();
  const { enabled: brandsEnabled, brands } = useBrand();
  const brandById = useMemo(
    () => brandsEnabled && brands.length > 1 ? new Map(brands.map((brand) => [brand.id, brand])) : new Map(),
    [brandsEnabled, brands]
  );

  const [tab, setTab] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [query, setQuery] = useState('');
  const [view, setView] = useState('grid');
  const sentinelRef = useRef(null);

  // Infinite scroll: when sentinel comes into view, fetch next page.
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && hasMore && !loading) {
            loadMore?.();
          }
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, content?.length]);

  // Re-fetch when tab changes (server-side status filter)
  useEffect(() => {
    const tabSpec = TABS.find((t) => t.id === tab) || TABS[0];
    // "Drafts" tab covers two backend buckets: `draft` and `ready`.
    // The server filter only accepts ONE status so we fetch the full
    // feed in that case and narrow it down client-side below.
    const serverStatus = tabSpec.status === 'draft' ? 'all' : tabSpec.status;
    refreshContent({ status: serverStatus, background: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabsWithCounts = useMemo(() => {
    const c = counts || {};
    return TABS.map((t) => {
      // "Drafts" is the user-friendly name for two backend buckets:
      // - `draft`   = content_item.status='draft' (manual draft)
      // - `ready`   = generation_status='ready' (AI-generated, not yet
      //              scheduled or published)
      // Both look like "Draft" in the UI badge — so the count should
      // include both, and the tab filter should fetch both.
      let count;
      if (t.status === 'all') count = c.all || 0;
      else if (t.status === 'draft') count = (c.draft || 0) + (c.ready || 0);
      else count = c[t.status] || 0;
      return { ...t, count };
    });
  }, [counts]);

  // Expand each content row into per-target cards, then filter client-side
  // by platform + query. (Server already filtered status via refreshContent.)
  // Title + snippet are computed per-card so a multi-target post (e.g. one
  // content_item with separate Instagram/Threads/Telegram variants) shows
  // each platform's own copy instead of a single shared blurb.
  const filteredCards = useMemo(() => {
    const cards = (content || []).flatMap((row) => {
      const expanded = expandContentToCards(row);
      return expanded.map((card) => ({
        ...card,
        _title: deriveTitle(row, card.platform, t),
        _snippet: deriveSnippet(row, card.platform),
      }));
    });
    const visible = cards.filter((card) => {
      // Drafts tab: keep both backend buckets (draft + ready), drop
      // everything else. We fetched all because the server filter
      // can't accept two statuses.
      if (tab === 'draft') {
        const s = (card.status || '').toLowerCase();
        // 'held' (waiting for image) is a draft in the backend — keep it here.
        if (s !== 'draft' && s !== 'ready' && s !== 'held') return false;
      } else if (tab === 'attention') {
        if (!['failed', 'attention', 'needs_check', 'inbox'].includes(card.status)) return false;
      } else if (tab !== 'all' && card.status !== tab) {
        return false;
      }
      if (platform !== 'all' && card.platform !== platform) return false;
      if (query) {
        const needle = query.toLowerCase();
        if (!card._title.toLowerCase().includes(needle) && !card._snippet.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
    // Newest first by effective date (see libraryOrderTs) — replaces the raw
    // feed order that surfaced old posts above a freshly-generated week.
    return visible.sort((a, b) => libraryOrderTs(b) - libraryOrderTs(a));
  }, [content, platform, query, tab, t]);
  const hasMorePublicationHistory = (content || []).some((row) => row.publishSummary?.hasMoreTargets);

  // These are loaded, filtered publications, not an all-time server total.
  const summary = useMemo(() => {
    const published = filteredCards.filter(card => card.status === 'published');
    const rates = published.map(card => engagementRate(card.metrics)).filter(rate => rate !== null);
    return {
      published: hasLoaded ? counts?.published || 0 : '—',
      metrics: aggregateMetrics(published.map(card => card.metrics), { emptyIsZero: hasLoaded && !loading && !hasMore, partial: !!hasMore }),
      avgEng: rates.length === published.length && rates.length ? rates.reduce((sum, rate) => sum + rate, 0) / rates.length : null,
    };
  }, [counts, filteredCards, loading, hasLoaded, hasMore]);
  const summaryViews = readMetric(summary.metrics, 'views');
  const viewsValue = formatMetric(summaryViews.value, i18n.language);
  const missingViews = filteredCards.some(card => card.status === 'published' && readMetric(card.metrics, 'views').value === null);

  return (
    <div className="lib-root">
      <TikTokStatisticsBanner />
      <div className="lib-top">
        <div>
          <h1 className="lib-h">{t('library.title')}</h1>
          <p className="lib-sub">
            {t('library.subtitle')}
          </p>
        </div>
        <div className="lib-stats-wrap">
          <div className="lib-stats">
            <div className="lib-stat">
              <div className="lib-stat-l">{t('library.statPublished')}</div>
              <div className="lib-stat-v">
                {summary.published}
                <span className="lib-stat-u">{t('library.statPublishedUnit')}</span>
              </div>
            </div>
            <div className="lib-stat">
              <div className="lib-stat-l">{t('socialMetrics.views')}</div>
              <div className="lib-stat-v">
                <span className="metric-reading is-compact" role="group" data-metric="views"
                  data-state={summaryViews.value === null ? 'unavailable' : 'measured'}
                  aria-label={`${t('socialMetrics.views')}: ${summaryViews.value === null ? t('socialMetrics.unavailable') : viewsValue}`}
                  aria-describedby={hasLoaded ? 'library-views-note' : undefined}>
                  <span className="mono metric-number" aria-hidden="true">{viewsValue}</span>
                </span>
              </div>
            </div>
            <div className="lib-stat">
              <div className="lib-stat-l">{t('library.statAvgEngagement')}</div>
              <div className="lib-stat-v">
                {summary.avgEng != null ? summary.avgEng.toFixed(1) : '—'}
                <span className="lib-stat-u">%</span>
              </div>
            </div>
          </div>
          {hasLoaded && <p className="lib-stats-note" id="library-views-note">
            {t('library.visiblePostsMetricHint')}{missingViews ? ` ${t('library.missingViewDataHint')}` : ''}
          </p>}
        </div>
      </div>

      {metricRefreshFeedback && <p className="metric-feedback" role="status">{t(`socialMetrics.${metricRefreshFeedback}`)}</p>}
      {error && hasLoaded && <p className="metric-feedback" role="status">{t('socialMetrics.readFailed')}</p>}
      <div className="lib-tabs">
        {tabsWithCounts.map((tabItem) => (
          <button
            key={tabItem.id}
            className={`lib-tab ${tab === tabItem.id ? 'is-active' : ''}`}
            onClick={() => setTab(tabItem.id)}
            type="button"
          >
            {t(tabItem.labelKey)}
            <span className="lib-tab-c mono">{hasLoaded ? tabItem.count : '—'}</span>
          </button>
        ))}
      </div>

      <div className="lib-bar">
        <div className="lib-search">
          <I.search size={14} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('library.searchPlaceholder')}
          />
          {query && (
            <button
              className="lib-search-clear"
              onClick={() => setQuery('')}
              type="button"
              aria-label={t('library.clearSearch')}
            >
              <I.x size={12} />
            </button>
          )}
        </div>

        <div className="lib-plat-filter">
          {PLATFORMS.map((p) => (
            <button
              key={p}
              className={`lib-plat-chip ${platform === p ? 'is-active' : ''}`}
              onClick={() => setPlatform(p)}
              title={p === 'all' ? t('library.platformAll') : platformDisplayName(p)}
              type="button"
            >
              {p === 'all' ? (
                <span style={{ fontSize: 11, padding: '0 4px' }}>{t('library.platformAll')}</span>
              ) : (
                <span
                  className={`ob-net-ic-${p}`}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  {BrandIcons[p]?.({ size: 11 })}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="lib-bar-r">
          <div className="cp-view-toggle">
            <button
              className={view === 'grid' ? 'is-active' : ''}
              onClick={() => setView('grid')}
              title={t('library.viewGrid')}
              type="button"
            >
              <I.grid size={13} />
            </button>
            <button
              className={view === 'list' ? 'is-active' : ''}
              onClick={() => setView('list')}
              title={t('library.viewList')}
              type="button"
            >
              <I.list size={13} />
            </button>
          </div>
        </div>
      </div>

      {loading && (!content || content.length === 0) ? (
        <div className="lib-empty">
          <I.refresh size={20} />
          <h3>{t('library.loading')}</h3>
        </div>
      ) : !hasLoaded ? (
        <div className="lib-empty" role="alert">
          <I.alert size={20} />
          <h3>{t('socialMetrics.unavailable')}</h3>
          <p>{t('socialMetrics.readFailed')}</p>
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => refreshContent({ refreshMetrics: false })}>
            {t('postPreview.refresh')}
          </button>
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="lib-empty">
          <I.library size={28} />
          <h3>{t('library.emptyTitle')}</h3>
          <p>{t('library.emptyDescription')}</p>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setTab('all');
              setPlatform('all');
              setQuery('');
            }}
            type="button"
          >
            {t('library.clearFilters')}
          </button>
        </div>
      ) : (
        <>
          <div className={`lib-grid ${view === 'list' ? 'is-list' : ''}`}>
            {filteredCards.map((card) => {
              const row = card.contentRow;
              const item = row.contentItem || row;
              const id = item.id;
              const status = card.status;
              // Note: PostPreview now renders its own "still generating"
              // overlay for mid-flight posts (task #76), so we don't need
              // the Library to defensively route to the batch screen when
              // the post isn't fully ready. The preview eye-icon ALWAYS
              // takes the user to /app/post/:id.
              // Preserve platform/target hint on the URL so PostPreview and
              // EditPost can scope their chrome (and the per-account picker)
              // to THIS specific row instead of falling back to the first
              // platform in the batch.
              const hintQs = (() => {
                const params = new URLSearchParams();
                if (card.routeTargetId || card.targetId) params.set('target', card.routeTargetId || card.targetId);
                if (card.platform) params.set('platform', card.platform);
                return params.toString();
              })();
              const hintSuffix = hintQs ? `?${hintQs}` : '';
              const onPreview = () => {
                navigate(`/app/post/${encodeURIComponent(id)}${hintSuffix}`);
              };
              const onEdit = () => navigate(`/app/post/${encodeURIComponent(id)}/edit${hintSuffix}`);
              // TASK-074 held card CTA routes straight into the EXISTING
              // media attach/replace flow (EditPost media tab). The full
              // held panel (explanation + text-only opt-in) lives on the
              // post preview, which the card cover/eye opens.
              const onAddImage = () => navigate(`/app/post/${encodeURIComponent(id)}/edit?tab=media${hintQs ? `&${hintQs}` : ''}`);
              const actionKind =
                status === 'attention' || status === 'failed' || status === 'inbox' || status === 'needs_check' ? 'review' :
                status === 'held' ? 'addImage' :
                status === 'draft' ? 'edit' :
                status === 'published' ? 'open' :
                status === 'scheduled' || status === 'planned' || status === 'ready' ? 'reschedule' :
                null;
              const actionLabel =
                actionKind === 'review' ? t('library.actionReview') :
                actionKind === 'addImage' ? t('library.actionAddImage') :
                actionKind === 'edit' ? t('library.actionEdit') :
                actionKind === 'open' ? t('library.actionOpen') :
                actionKind === 'reschedule' ? t('library.actionReschedule') :
                null;
              const videoUrl = deriveVideoSource(row);
              const coverImage = deriveVideoPoster(row, videoUrl);
              const cardTitle = card._title;
              const fullSnippet = card._snippet;
              const showSnippet =
                fullSnippet && !cardTitle.replace(/…$/, '').startsWith(fullSnippet.slice(0, 30))
                  && !fullSnippet.startsWith(cardTitle.replace(/…$/, ''));
              return (
                <LibraryCard
                  key={card.key}
                  id={id}
                  platform={card.platform}
                  status={status}
                  title={cardTitle}
                  snippet={showSnippet ? fullSnippet : ''}
                  date={deriveCardDate(card, t)}
                  media={videoUrl ? 'video' : deriveMedia(row)}
                  metrics={card.metrics}
                  imageUrl={coverImage}
                  videoUrl={coverImage ? null : videoUrl}
                  brand={brandById.get(item.brandProfileId)}
                  href={(() => {
                    const params = new URLSearchParams();
                    if (card.routeTargetId || card.targetId) params.set('target', card.routeTargetId || card.targetId);
                    if (card.platform) params.set('platform', card.platform);
                    const qs = params.toString();
                    return `/app/post/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
                  })()}
                  onPreview={onPreview}
                  onEdit={onEdit}
                  onAction={
                    actionKind === 'addImage' ? onAddImage :
                    actionKind === 'edit' ? onEdit :
                    actionKind === 'open' || actionKind === 'review' ? onPreview :
                    actionKind === 'reschedule' ? () => navigate('/app/calendar') :
                    null
                  }
                  actionLabel={actionLabel}
                />
              );
            })}
          </div>

          {hasMorePublicationHistory && <p className="lib-history-note" role="note">
            {t('library.morePublicationHistory')}{' '}
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/app/calendar')} type="button">
              {t('library.openCalendarHistory')}
            </button>
          </p>}

          {/* Infinite-scroll sentinel + loading footer */}
          {hasMore && (
            <div
              ref={sentinelRef}
              style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-subtle)', fontSize: 12 }}
            >
              {loading ? t('library.loadingMore') : t('library.scrollForMore')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Library;
